import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "./pdf-forms.css";
import { Loader2, AlertTriangle, ZoomIn, ZoomOut, Maximize2, Lock, Unlock } from "lucide-react";
import TakeoffOverlay from "./TakeoffOverlay";

pdfjs.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;


function getAuthHeaders() {
    try {
        const t = localStorage.getItem("session_token") || sessionStorage.getItem("session_token");
        return t ? { Authorization: `Bearer ${t}` } : {};
    } catch {
        return {};
    }
}

const MIN_SCALE = 0.1;
const MAX_SCALE = 5;
const SCALE_STEP = 0.25;
const FIT_PADDING = 24;

export const PdfViewer = ({
    url,
    testId = "pdf-viewer",
    targetPage = 1,
    takeoffItems = [],
    selectedTakeoffId = null,
    onSelectTakeoff,
    onCommitTakeoffPoints,
    unitSystem = "imperial",
    drawMode = null,
    drawTrade = "earthwork",
    drawPoints = [],
    onDrawPoint,
    onDrawFinish,
    onPageSize,
    // iter-169e — when true, render pdf.js's native annotation layer
    // so AcroForm text fields / checkboxes are interactive. Off by
    // default (takeoff canvas doesn't want the annotation layer),
    // on for Schedule 1/2/3 uploads.
    interactiveForm = false,
    onFormFieldChange,
}) => {
    const [numPages, setNumPages] = useState(0);
    const [error, setError] = useState(null);
    const [scale, setScale] = useState(1);
    const [locked, setLocked] = useState(false);
    const [pageSize, setPageSize] = useState(null);

    const scrollRef = useRef(null);
    const contentRef = useRef(null);
    const didAutoFitRef = useRef(false);
    // When a zoom is committed we store the target scrollLeft/scrollTop that
    // keeps the user's focal point stationary. The scroll is applied in a
    // layout effect after React paints the new scale — that way we always win
    // the race with react-pdf re-rendering its canvas (previously we lost the
    // race and the scroll snapped to 0 / back to the page top).
    const pendingScrollRef = useRef(null);

    // Two-finger pinch state
    const pointersRef = useRef(new Map());
    const pinchRef = useRef(null); // { startDist, startScale, originX, originY, liveFactor }
    const rafRef = useRef(null);

    useEffect(() => {
        setNumPages(0);
        setError(null);
        setScale(1);
        setPageSize(null);
        didAutoFitRef.current = false;
        pendingScrollRef.current = null;
    }, [url]);

    // Scroll to the requested page when targetPage changes (from sidebar
    // clicks). DO NOT include `scale` in the deps — every zoom commit would
    // otherwise re-scroll to the top of the active page and wipe out the
    // focal-point correction the pinch handler just applied.
    useEffect(() => {
        if (!numPages || targetPage < 1 || targetPage > numPages) return;
        const run = () => {
            const el = scrollRef.current?.querySelector(
                `[data-page-index="${targetPage - 1}"]`,
            );
            if (el && scrollRef.current) {
                const top = el.offsetTop - 12;
                scrollRef.current.scrollTo({ top, behavior: "smooth" });
            }
        };
        // Let the page finish rendering/measure before scrolling.
        const t = setTimeout(run, 120);
        return () => clearTimeout(t);
    }, [targetPage, numPages]);

    // iter-169e — AcroForm edit capture. When `interactiveForm` is on
    // (Schedule 1/2/3), pdf.js renders each field as a real <input>
    // inside the annotation layer. Listen for change/input events on
    // the content wrapper and forward {name, value} up so
    // WorkspacePage can debounce-save to the backend.
    useEffect(() => {
        if (!interactiveForm || !onFormFieldChange) return;
        const el = contentRef.current;
        if (!el) return;
        const handler = (ev) => {
            const t = ev.target;
            if (!t || !t.name) return;
            if (t.tagName === "INPUT" && t.type === "text") {
                onFormFieldChange(t.name, t.value);
            } else if (t.tagName === "INPUT" && t.type === "checkbox") {
                onFormFieldChange(t.name, t.checked ? "/Yes" : "/Off");
            } else if (t.tagName === "TEXTAREA") {
                onFormFieldChange(t.name, t.value);
            } else if (t.tagName === "SELECT") {
                onFormFieldChange(t.name, t.value);
            }
        };
        el.addEventListener("change", handler);
        el.addEventListener("input", handler);
        return () => {
            el.removeEventListener("change", handler);
            el.removeEventListener("input", handler);
        };
    }, [interactiveForm, onFormFieldChange, numPages]);

    const clampScale = (v) => Math.max(MIN_SCALE, Math.min(MAX_SCALE, +v.toFixed(3)));

    // Stage a scroll correction that will be applied after React commits the
    // new scale AND react-pdf has re-rendered the page at that scale.
    // Focal coords are in container-viewport pixels (i.e. relative to the top
    // left of the scroll container, NOT including scrollLeft/scrollTop).
    const commitScaleAt = useCallback((nextScale, focalX, focalY) => {
        const container = scrollRef.current;
        const target = clampScale(nextScale);
        if (!container) {
            setScale(target);
            return;
        }
        if (target === scale) return;
        const factor = target / scale;
        const prevLeft = container.scrollLeft;
        const prevTop = container.scrollTop;
        pendingScrollRef.current = {
            left: (prevLeft + focalX) * factor - focalX,
            top: (prevTop + focalY) * factor - focalY,
            // Cap the number of frames we'll keep retrying to apply the scroll
            // while waiting for react-pdf's canvas to finish resizing. 12
            // frames ≈ 200ms at 60fps — plenty of time for the new canvas to
            // land while still feeling instant to the user.
            tries: 12,
        };
        setScale(target);
    }, [scale]);

    // Keeps the focal point locked in place across the zoom. Runs every time
    // `scale` changes — we apply the pending scroll as soon as the content's
    // scrollable area has grown/shrunk to match the new scale, and retry for
    // a few frames if the canvas isn't ready yet.
    useLayoutEffect(() => {
        const pending = pendingScrollRef.current;
        if (!pending) return;
        let cancelled = false;
        const tryApply = () => {
            if (cancelled) return;
            const c = scrollRef.current;
            if (!c) return;
            // Clamp to valid range so we don't fight the browser's own
            // scroll-position clamping when content becomes smaller.
            const maxLeft = Math.max(0, c.scrollWidth - c.clientWidth);
            const maxTop = Math.max(0, c.scrollHeight - c.clientHeight);
            c.scrollLeft = Math.max(0, Math.min(maxLeft, pending.left));
            c.scrollTop = Math.max(0, Math.min(maxTop, pending.top));
            pending.tries -= 1;
            if (pending.tries > 0) {
                requestAnimationFrame(tryApply);
            } else {
                pendingScrollRef.current = null;
            }
        };
        requestAnimationFrame(tryApply);
        return () => {
            cancelled = true;
        };
    }, [scale]);

    const commitScaleAtCenter = useCallback((nextScale) => {
        const c = scrollRef.current;
        if (!c) return setScale(clampScale(nextScale));
        commitScaleAt(nextScale, c.clientWidth / 2, c.clientHeight / 2);
    }, [commitScaleAt]);

    const zoomIn = () => commitScaleAtCenter(scale + SCALE_STEP);
    const zoomOut = () => commitScaleAtCenter(scale - SCALE_STEP);
    const resetZoom = () => commitScaleAtCenter(1);

    const fitToWindow = useCallback(() => {
        const container = scrollRef.current;
        if (!container || !pageSize) return;
        const availW = container.clientWidth - FIT_PADDING * 2;
        const availH = container.clientHeight - FIT_PADDING * 2;
        if (availW <= 0 || availH <= 0) return;
        const ratio = Math.min(availW / pageSize.width, availH / pageSize.height);
        pendingScrollRef.current = null; // fit explicitly resets scroll to 0,0
        setScale(clampScale(ratio));
        requestAnimationFrame(() => {
            container.scrollTo({ top: 0, left: 0, behavior: "auto" });
        });
    }, [pageSize]);

    useEffect(() => {
        if (pageSize && !didAutoFitRef.current) {
            didAutoFitRef.current = true;
            requestAnimationFrame(() => fitToWindow());
        }
    }, [pageSize, fitToWindow]);

    // --- Pinch-zoom: live CSS transform during gesture, commit on release.
    const applyLiveTransform = (factor, contentOriginX, contentOriginY) => {
        const el = contentRef.current;
        if (!el) return;
        el.style.transformOrigin = `${contentOriginX}px ${contentOriginY}px`;
        el.style.transform = `scale(${factor})`;
        el.style.willChange = "transform";
    };

    const clearLiveTransform = () => {
        const el = contentRef.current;
        if (!el) return;
        el.style.transform = "";
        el.style.transformOrigin = "";
        el.style.willChange = "";
    };

    const onPointerDown = (e) => {
        if (locked) return;
        pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pointersRef.current.size === 2) {
            const [a, b] = Array.from(pointersRef.current.values());
            const dist = Math.hypot(a.x - b.x, a.y - b.y) || 1;
            const rect = scrollRef.current.getBoundingClientRect();
            const focalViewportX = (a.x + b.x) / 2 - rect.left;
            const focalViewportY = (a.y + b.y) / 2 - rect.top;
            // Container-coords origin (scroll offset included) so setting it
            // as transform-origin on the content keeps the focal point stable.
            const originX = scrollRef.current.scrollLeft + focalViewportX;
            const originY = scrollRef.current.scrollTop + focalViewportY;
            pinchRef.current = {
                startDist: dist,
                startScale: scale,
                originX,
                originY,
                focalViewportX,
                focalViewportY,
                liveFactor: 1,
            };
            try {
                e.currentTarget.setPointerCapture(e.pointerId);
            } catch {
                /* ignore */
            }
        }
    };

    const schedulePinchFrame = () => {
        if (rafRef.current != null) return;
        rafRef.current = requestAnimationFrame(() => {
            rafRef.current = null;
            if (!pinchRef.current) return;
            applyLiveTransform(
                pinchRef.current.liveFactor,
                pinchRef.current.originX,
                pinchRef.current.originY,
            );
        });
    };

    const onPointerMove = (e) => {
        if (!pointersRef.current.has(e.pointerId)) return;
        pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pointersRef.current.size === 2 && pinchRef.current) {
            e.preventDefault();
            const [a, b] = Array.from(pointersRef.current.values());
            const dist = Math.hypot(a.x - b.x, a.y - b.y) || 1;
            const rawFactor = dist / pinchRef.current.startDist;
            // Clamp so live visual can't exceed commitable bounds.
            const committed = pinchRef.current.startScale;
            const minFactor = MIN_SCALE / committed;
            const maxFactor = MAX_SCALE / committed;
            pinchRef.current.liveFactor = Math.max(
                minFactor,
                Math.min(maxFactor, rawFactor),
            );
            schedulePinchFrame();
        }
    };

    const endPointer = (e) => {
        const hadPinch = pointersRef.current.size === 2 && pinchRef.current;
        pointersRef.current.delete(e.pointerId);
        if (pointersRef.current.size < 2 && hadPinch) {
            // Commit the final factor to a real render scale.
            const p = pinchRef.current;
            pinchRef.current = null;
            if (rafRef.current != null) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
            clearLiveTransform();
            const finalScale = clampScale(p.startScale * p.liveFactor);
            commitScaleAt(finalScale, p.focalViewportX, p.focalViewportY);
        }
    };

    // Desktop: Planswift-style mouse wheel = zoom (no Ctrl needed).
    // When the magnifier is on, the wheel zooms the magnifier only.
    const onWheel = (e) => {
        if (magnifierOn) {
            e.preventDefault();
            const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
            setMagnifierZoom((z) => Math.max(1.2, Math.min(12, z * factor)));
            return;
        }
        if (locked) {
            e.preventDefault();
            return;
        }
        e.preventDefault();
        const rect = scrollRef.current.getBoundingClientRect();
        const focalX = e.clientX - rect.left;
        const focalY = e.clientY - rect.top;
        const factor = e.deltaY < 0 ? 1.08 : 1 / 1.08;
        commitScaleAt(scale * factor, focalX, focalY);
    };

    // --- Planswift-style mouse interactions ---
    // Track live mouse position so the magnifier follows the cursor.
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
    const panningRef = useRef(null);

    // M-key magnifier (hold to show, release to hide). Scroll wheel while
    // the magnifier is open changes ONLY the magnifier zoom, leaving the
    // main plan's zoom alone — matching Planswift's behaviour.
    const [magnifierOn, setMagnifierOn] = useState(false);
    const [magnifierZoom, setMagnifierZoom] = useState(2.5);
    const magCanvasRef = useRef(null);

    useEffect(() => {
        const isEditable = (el) => {
            if (!el) return false;
            const tag = el.tagName;
            if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
            return !!el.isContentEditable;
        };
        const onKeyDown = (e) => {
            if (e.repeat) return;
            if ((e.key === "m" || e.key === "M") && !isEditable(e.target)) {
                e.preventDefault();
                setMagnifierOn(true);
            }
        };
        const onKeyUp = (e) => {
            if ((e.key === "m" || e.key === "M") && !isEditable(e.target)) {
                e.preventDefault();
                setMagnifierOn(false);
            }
        };
        window.addEventListener("keydown", onKeyDown);
        window.addEventListener("keyup", onKeyUp);
        return () => {
            window.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("keyup", onKeyUp);
        };
    }, []);

    // Click-and-drag panning — RIGHT and MIDDLE mouse buttons pan. LEFT
    // click is reserved for future in-canvas editing (drag shape
    // corners / endpoints, click-to-select, etc.) so the viewer stops
    // hijacking it. Matches Planswift/AutoCAD-style workflows where
    // pan-with-right/middle is muscle memory.
    const onMouseDown = (e) => {
        // Ignore if over an interactive child (buttons, inputs). We only
        // want panning on the PDF canvas itself.
        const target = e.target;
        if (target && target.closest && target.closest("button,input,select,textarea,a")) {
            return;
        }
        // Right (button 2) + Middle (button 1) pan. Left click is now
        // reserved for shape editing.
        if (e.button !== 1 && e.button !== 2) return;
        if (magnifierOn) return;
        // Only start a pan when the PDF content is bigger than the viewport,
        // otherwise a normal click should still be possible on empty space.
        const el = scrollRef.current;
        if (!el) return;
        const panX = el.scrollWidth > el.clientWidth;
        const panY = el.scrollHeight > el.clientHeight;
        if (!panX && !panY) return;
        e.preventDefault();
        panningRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            scrollLeft: el.scrollLeft,
            scrollTop: el.scrollTop,
        };
        el.style.cursor = "grabbing";
        el.setPointerCapture?.(e.pointerId);
    };

    const onMouseMove = (e) => {
        // Track pointer for the magnifier first (runs even when not panning).
        if (scrollRef.current) {
            const rect = scrollRef.current.getBoundingClientRect();
            setMousePos({ x: e.clientX - rect.left, y: e.clientY - rect.top });
        }
        const p = panningRef.current;
        if (!p) return;
        const el = scrollRef.current;
        if (!el) return;
        el.scrollLeft = p.scrollLeft - (e.clientX - p.startX);
        el.scrollTop = p.scrollTop - (e.clientY - p.startY);
    };

    const endPan = () => {
        panningRef.current = null;
        if (scrollRef.current) scrollRef.current.style.cursor = "";
    };

    // Paint the magnifier content: grab the PDF canvas under the cursor and
    // redraw the region around it, zoomed, into our own small canvas. Runs
    // on every mouse move / zoom change while the magnifier is open.
    useEffect(() => {
        if (!magnifierOn) return;
        const cv = magCanvasRef.current;
        const host = scrollRef.current;
        if (!cv || !host) return;
        const rect = host.getBoundingClientRect();
        // Use elementFromPoint to grab whichever pdfjs page-canvas is under
        // the cursor right now (works even across page boundaries).
        const pageEl = document.elementFromPoint(
            rect.left + mousePos.x,
            rect.top + mousePos.y,
        );
        const srcCanvas = pageEl && pageEl.closest("canvas");
        const ctx = cv.getContext("2d");
        ctx.fillStyle = "#0A1128";
        ctx.fillRect(0, 0, cv.width, cv.height);
        if (!srcCanvas) return;
        const cRect = srcCanvas.getBoundingClientRect();
        const sx = ((rect.left + mousePos.x) - cRect.left) * (srcCanvas.width / cRect.width);
        const sy = ((rect.top + mousePos.y) - cRect.top) * (srcCanvas.height / cRect.height);
        const srcW = cv.width / magnifierZoom;
        const srcH = cv.height / magnifierZoom;
        try {
            ctx.drawImage(
                srcCanvas,
                sx - srcW / 2,
                sy - srcH / 2,
                srcW,
                srcH,
                0,
                0,
                cv.width,
                cv.height,
            );
        } catch {
            /* cross-origin or other — nothing to do */
        }
    }, [magnifierOn, mousePos, magnifierZoom]);

    const dpr = typeof window !== "undefined" ? Math.max(1, window.devicePixelRatio || 1) : 1;

    return (
        <div
            className={`w-full h-full flex flex-col bg-slate-200 relative ${
                locked ? "ring-2 ring-amber-500/70 ring-inset" : ""
            }`}
            data-testid={testId}
            data-locked={locked ? "true" : "false"}
        >
            {/* Zoom toolbar */}
            <div className="absolute top-2 right-2 z-10 flex items-center gap-1 bg-[#111] backdrop-blur-sm border border-white/15 rounded-md p-1 shadow-lg">                <button
                    type="button"
                    onClick={zoomOut}
                    aria-label="Zoom out"
                    data-testid={`${testId}-zoom-out`}
                    className="w-8 h-8 rounded-sm flex items-center justify-center text-white hover:bg-white/10 active:scale-95 transition-colors disabled:opacity-40"
                    disabled={scale <= MIN_SCALE || locked}
                >
                    <ZoomOut className="w-4 h-4" />
                </button>
                <button
                    type="button"
                    onClick={resetZoom}
                    aria-label="Reset to 100%"
                    data-testid={`${testId}-zoom-reset`}
                    className="px-2 h-8 rounded-sm text-white text-xs font-mono font-bold hover:bg-white/10 active:scale-95 transition-colors min-w-[52px] disabled:opacity-40"
                    title="Reset to 100%"
                    disabled={locked}
                >
                    {Math.round(scale * 100)}%
                </button>
                <button
                    type="button"
                    onClick={zoomIn}
                    aria-label="Zoom in"
                    data-testid={`${testId}-zoom-in`}
                    className="w-8 h-8 rounded-sm flex items-center justify-center text-white hover:bg-white/10 active:scale-95 transition-colors disabled:opacity-40"
                    disabled={scale >= MAX_SCALE || locked}
                >
                    <ZoomIn className="w-4 h-4" />
                </button>
                <div className="w-px h-5 bg-[#1a1a1a] mx-0.5" />
                <button
                    type="button"
                    onClick={fitToWindow}
                    aria-label="Fit to window"
                    data-testid={`${testId}-fit`}
                    title="Fit to window"
                    disabled={!pageSize || locked}
                    className="h-8 px-2 rounded-sm flex items-center gap-1.5 text-white hover:bg-white/10 active:scale-95 transition-colors disabled:opacity-40"
                >
                    <Maximize2 className="w-4 h-4" />
                    <span className="text-xs font-semibold hidden sm:inline">Fit</span>
                </button>
                <div className="w-px h-5 bg-[#1a1a1a] mx-0.5" />
                <button
                    type="button"
                    onClick={() => setLocked((v) => !v)}
                    aria-label={locked ? "Unlock document (exit edit mode)" : "Lock document (edit mode)"}
                    aria-pressed={locked}
                    data-testid={`${testId}-lock`}
                    title={locked ? "Edit mode — tap to unlock" : "Lock & edit"}
                    className={`h-8 px-2 rounded-sm flex items-center gap-1.5 transition-colors active:scale-95 ${
                        locked
                            ? "bg-amber-500/90 text-[#0A1128] font-bold shadow-[0_0_14px_rgba(245,158,11,0.45)]"
                            : "text-white hover:bg-white/10"
                    }`}
                >
                    {locked ? (
                        <Lock className="w-4 h-4" />
                    ) : (
                        <Unlock className="w-4 h-4" />
                    )}
                    <span className="text-xs font-semibold hidden sm:inline">
                        {locked ? "Edit" : "Lock"}
                    </span>
                </button>
            </div>

            {/* Keyboard + mouse hint (hidden on touch, visible once mouse has moved) */}
            <div
                className="absolute bottom-2 left-2 z-10 hidden md:flex items-center gap-2 bg-[#111] backdrop-blur-sm border border-white/15 rounded px-2 py-1 text-[10px] text-slate-400 pointer-events-none select-none"
                data-testid={`${testId}-mouse-hint`}
            >
                <span>Wheel = zoom</span>
                <span className="opacity-40">·</span>
                <span>Right / middle drag = pan</span>
                <span className="opacity-40">·</span>
                <span>
                    Hold <kbd className="px-1 py-0.5 bg-[#0a0a0a] border border-white/15 rounded text-[#8fa4f0] font-bold">M</kbd> = magnifier
                </span>
            </div>


            <div
                ref={scrollRef}
                className={`flex-1 min-h-0 ${locked ? "overflow-hidden" : "overflow-auto"} ${
                    magnifierOn ? "cursor-zoom-in" : ""
                } ${interactiveForm ? "pdf-forms-interactive" : ""}`}
                style={{
                    touchAction: locked ? "none" : "pan-x pan-y",
                    overscrollBehavior: "contain",
                }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={endPointer}
                onPointerCancel={endPointer}
                onPointerLeave={endPointer}
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={endPan}
                onMouseLeave={endPan}
                onWheel={onWheel}
                onContextMenu={(e) => {
                    // Right-click is now a pan gesture, not a menu trigger —
                    // always suppress the browser context menu over the PDF
                    // canvas so right-drag works unobstructed.
                    e.preventDefault();
                }}
            >
                {error ? (
                    <div className="flex items-center justify-center h-full text-center text-slate-700 p-6">
                        <div>
                            <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-amber-500" />
                            <div className="font-semibold">Couldn't preview PDF</div>
                            <div className="text-xs text-slate-500 mt-1">{error}</div>
                            <a
                                href={url}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-3 inline-block text-sm font-semibold text-[#8fa4f0] hover:underline"
                            >
                                Open in new tab
                            </a>
                        </div>
                    </div>
                ) : (
                    <div ref={contentRef} className="inline-block min-w-full p-3">
                        <Document
                            file={url}
                            onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                            onLoadError={(e) => setError(e?.message || "Load failed")}
                            loading={
                                <div className="flex items-center gap-2 text-slate-600 py-12 justify-center">
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                    <span className="text-sm">Loading PDF…</span>
                                </div>
                            }
                            className="flex flex-col items-start gap-3"
                        >
                            {Array.from({ length: numPages }, (_, i) => (
                                <div
                                    key={i + 1}
                                    className="shadow-md border border-slate-300 bg-white relative"
                                    data-testid={`${testId}-page-${i + 1}`}
                                    data-page-index={i}
                                >
                                    <Page
                                        pageNumber={i + 1}
                                        scale={scale}
                                        devicePixelRatio={dpr}
                                        renderTextLayer={interactiveForm}
                                        renderAnnotationLayer={interactiveForm}
                                        renderForms={interactiveForm}
                                        onLoadSuccess={(pg) => {
                                            if (i === 0 && pg) {
                                                const vp = pg.getViewport
                                                    ? pg.getViewport({ scale: 1 })
                                                    : null;
                                                const w = vp?.width || pg.width;
                                                const h = vp?.height || pg.height;
                                                if (w && h) {
                                                    setPageSize((prev) =>
                                                        prev && prev.width === w && prev.height === h
                                                            ? prev
                                                            : { width: w, height: h },
                                                    );
                                                    if (onPageSize) onPageSize({ width: w, height: h });
                                                }
                                            }
                                        }}
                                    />
                                    {/* AI takeoff shapes for this page (if any).
                                        Items coming through filter on their own
                                        page_number, but because every split-page
                                        PDF is ALWAYS single-page, items attached
                                        to this viewer belong on page 1. */}
                                    {takeoffItems.length > 0 && i === 0 && (
                                        <TakeoffOverlay
                                            items={takeoffItems}
                                            selectedId={selectedTakeoffId}
                                            onSelect={onSelectTakeoff}
                                            onPointsCommit={onCommitTakeoffPoints}
                                            pageWidth={pageSize?.width}
                                            pageHeight={pageSize?.height}
                                            unitSystem={unitSystem}
                                        />
                                    )}
                                    {/* Draw layer — only renders on the
                                        first page when the user is in a
                                        manual-draw mode. Captures clicks
                                        in PDF-pixel space (using the same
                                        coordinate system the AI uses) and
                                        feeds them up to WorkspacePage. */}
                                    {drawMode && i === 0 && pageSize && (
                                        <DrawLayer
                                            pageWidth={pageSize.width}
                                            pageHeight={pageSize.height}
                                            mode={drawMode}
                                            trade={drawTrade}
                                            points={drawPoints}
                                            onPoint={onDrawPoint}
                                            onFinish={onDrawFinish}
                                        />
                                    )}
                                </div>
                            ))}
                        </Document>
                    </div>
                )}
            </div>

            {magnifierOn && (
                <div
                    className="absolute pointer-events-none z-30 rounded-full border-[3px] border-[#1a3fd1]/90 shadow-[0_8px_28px_rgba(0,0,0,0.6)] overflow-hidden bg-[#0a0a0a]"
                    data-testid={`${testId}-magnifier`}
                    style={{
                        left: Math.max(0, mousePos.x - 110),
                        top: Math.max(0, mousePos.y - 110),
                        width: 220,
                        height: 220,
                    }}
                >
                    <canvas
                        ref={magCanvasRef}
                        width={220}
                        height={220}
                        className="block w-full h-full"
                    />
                    <div className="absolute bottom-1 right-2 text-[10px] font-bold tabular-nums text-[#8fa4f0] bg-[#111] px-1.5 rounded">
                        {magnifierZoom.toFixed(1)}x
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-3 h-3 border-2 border-[#1a3fd1]/50 rounded-full bg-transparent" />
                    </div>
                </div>
            )}

        </div>
    );
};

export default PdfViewer;

const TRADE_DRAW_COLORS = {
    earthwork: "#E67E22",
    concrete: "#A3A3A3",
    asphalt: "#2C3E50",
    framing: "#27AE60",
    drywall: "#F1C40F",
    roofing: "#8E44AD",
    plumbing: "#3498DB",
    electrical: "#E74C3C",
    hvac: "#1ABC9C",
    site_utilities: "#D35400",
    general: "#000000",
};

/**
 * Click-capture overlay used for manual takeoff drawing. Sits on top of
 * the page in the SAME coordinate system the AI emits items in (PDF
 * pixels at our render scale), so a manual shape and an AI shape are
 * indistinguishable once persisted.
 *
 * - `area` and `linear`: clicks accumulate as polygon/polyline vertices
 *   until the user hits Finish or double-clicks (auto-finishes).
 * - `count`: each click is a separate marker in one item.
 *
 * Points are converted from screen→viewBox space using the SVG's bounding
 * rect — this is identical to the math TakeoffOverlay uses for vertex
 * drag, so positions stay pixel-perfect across zoom/scroll.
 */
function DrawLayer({ pageWidth, pageHeight, mode, trade, points, onPoint, onFinish }) {
    const ref = React.useRef(null);
    const color = TRADE_DRAW_COLORS[trade] || "#00A3FF";

    const toViewBox = (evt) => {
        const svg = ref.current;
        if (!svg) return null;
        const rect = svg.getBoundingClientRect();
        const x = ((evt.clientX - rect.left) / rect.width) * pageWidth;
        const y = ((evt.clientY - rect.top) / rect.height) * pageHeight;
        return [
            Math.max(0, Math.min(x, pageWidth - 1)),
            Math.max(0, Math.min(y, pageHeight - 1)),
        ];
    };

    const handleClick = (e) => {
        e.stopPropagation();
        const xy = toViewBox(e);
        if (!xy || !onPoint) return;
        onPoint([Math.round(xy[0]), Math.round(xy[1])]);
    };
    const handleDoubleClick = (e) => {
        e.stopPropagation();
        e.preventDefault();
        // Double-click finishes the shape. The browser fires `click` once
        // before `dblclick`, so the second vertex got committed first —
        // which is what the user expects on the closing vertex.
        if (onFinish) onFinish();
    };

    // Render the in-progress preview so the user gets immediate feedback.
    let preview = null;
    if (mode === "area" && points.length >= 2) {
        const d =
            points.map((p, i) => `${i === 0 ? "M" : "L"}${p[0]},${p[1]}`).join(" ") +
            (points.length >= 3 ? " Z" : "");
        preview = (
            <path
                d={d}
                fill={`${color}33`}
                stroke={color}
                strokeWidth={3}
                strokeDasharray="6 4"
                vectorEffect="non-scaling-stroke"
            />
        );
    } else if (mode === "linear" && points.length >= 2) {
        const d = points.map((p, i) => `${i === 0 ? "M" : "L"}${p[0]},${p[1]}`).join(" ");
        preview = (
            <path
                d={d}
                fill="none"
                stroke={color}
                strokeWidth={4}
                strokeDasharray="6 4"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
            />
        );
    }

    return (
        <svg
            ref={ref}
            data-testid="takeoff-draw-layer"
            data-draw-mode={mode}
            viewBox={`0 0 ${pageWidth} ${pageHeight}`}
            preserveAspectRatio="none"
            className="absolute inset-0 w-full h-full"
            style={{
                cursor: "crosshair",
                pointerEvents: "auto",
                touchAction: "none",
            }}
            onClick={handleClick}
            onDoubleClick={handleDoubleClick}
        >
            {preview}
            {points.map((p, i) => (
                <circle
                    key={i}
                    cx={p[0]}
                    cy={p[1]}
                    r={6}
                    fill={color}
                    stroke="#FFFFFF"
                    strokeWidth={2}
                    vectorEffect="non-scaling-stroke"
                />
            ))}
        </svg>
    );
}

